// Bundled as a hashed asset; PDF.js parses documents in this worker, off the main thread.
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs" with { type: "file" };
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { Text } from "@/components/Text";

pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

// No eval: the CSP has no 'unsafe-eval', and font rendering works without it.
const OPTIONS = { isEvalSupported: false };

/**
 * Every page of a PDF, fitted to the panel's width. Rendered with PDF.js (not the browser's
 * built-in viewer) so it looks the same in every browser, including iOS Safari.
 */
export default function PdfPreview({ file }: Readonly<{ file: Blob }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number>();
  const [pages, setPages] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full">
      <Document
        file={file}
        options={OPTIONS}
        onLoadSuccess={({ numPages }) => setPages(numPages)}
        loading={<Text tone="tertiary">{t("loadingPreview")}</Text>}
        error={<Text tone="tertiary">{t("previewUnreadable")}</Text>}
      >
        {width &&
          Array.from({ length: pages }, (_, i) => (
            <Page
              // biome-ignore lint/suspicious/noArrayIndexKey: pages are identified by their number.
              key={i}
              pageNumber={i + 1}
              width={width}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              className="mb-4 overflow-hidden rounded-md shadow-md last:mb-0"
            />
          ))}
      </Document>
    </div>
  );
}
